// @ts-nocheck
/**
 * LoginForm Component
 * 
 * Login form with social OAuth providers (Google) and email/password.
 * Shows value proposition to new visitors before asking for sign-in.
 * Detects in-app browsers (KakaoTalk, Instagram, etc.) and shows an interstitial
 * prompting users to open in an external browser.
 * 
 * @author Heseda AI Team
 * @version 1.2
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, changeLanguage, type SupportedLanguage } from '../../i18n';
import { isInAppBrowser, signInWithEmail, signUpWithEmail, resetPassword } from '../../services/socialAuthService';
import type { EmailAuthError } from '../../services/socialAuthService';

const LANGUAGE_LABELS: Record<string, string> = {
  en: '🇺🇸 English',
  ko: '🇰🇷 한국어',
  'zh-CN': '🇨🇳 中文',
  'pt-BR': '🇧🇷 Português',
  es: '🇪🇸 Español',
  de: '🇩🇪 Deutsch',
  fr: '🇫🇷 Français',
  it: '🇮🇹 Italiano',
  nl: '🇳🇱 Nederlands',
  sv: '🇸🇪 Svenska',
  pl: '🇵🇱 Polski',
};

interface LoginFormProps {
  loginError: string | null;
  isSigningIn?: boolean;
  onGoogleSignIn: () => void;
}

/**
 * Login form with value proposition + social OAuth
 */
function LoginForm({
  loginError,
  isSigningIn = false,
  onGoogleSignIn,
}: LoginFormProps) {
  const { t, i18n } = useTranslation('common');
  const inApp = useMemo(() => isInAppBrowser(), []);
  const [linkCopied, setLinkCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Email auth state
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailMode, setEmailMode] = useState<'signin' | 'signup' | 'reset'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  useEffect(() => {
    return () => { if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current); };
  }, []);

  // Strip query/hash to avoid leaking OAuth state params or tokens
  const safeUrl = `${window.location.origin}${window.location.pathname}`;

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);
    setEmailLoading(true);
    try {
      if (emailMode === 'reset') {
        await resetPassword(email);
        setResetSent(true);
      } else if (emailMode === 'signup') {
        if (!displayName.trim()) {
          setEmailError(t('login.email.nameRequired', 'Name is required'));
          setEmailLoading(false);
          return;
        }
        await signUpWithEmail(email, password, displayName.trim());
      } else {
        await signInWithEmail(email, password);
      }
    } catch (err: unknown) {
      const authErr = err as EmailAuthError;
      const codeKey = authErr.code?.replace(/\//g, '_');
      const i18nKey = `login.email.errors.${codeKey}`;
      const translated = t(i18nKey, '');
      setEmailError(translated || authErr.message || t('login.errors.signInFailed', { message: 'Unknown error' }));
    } finally {
      setEmailLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(safeUrl);
      setLinkCopied(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setLinkCopied(false), 2000);
    } catch { /* clipboard access denied */ }
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const lang = e.target.value as SupportedLanguage;
    changeLanguage(lang)
      .then(() => { try { localStorage.setItem('heseda-ai-language-explicit', 'true'); } catch { /* */ } })
      .catch(() => { /* storage or i18n error */ });
  };

  // Show loading overlay during sign-in process
  if (isSigningIn) {
    return (
      <div className="login-container" role="main">
        <div className="login-form auth-loading-container">
          <img src="/logo-1024_simple.png" alt="" className="auth-loading-logo" width={48} height={48} />
          <p className="auth-loading-brand" aria-hidden="true">Heseda AI</p>
          <div className="auth-loading-spinner" aria-hidden="true"></div>
          <p className="auth-loading-text" role="status" aria-live="polite">{t('login.signingIn')}</p>
        </div>
      </div>
    );
  }

  // In-app browser (KakaoTalk, Instagram, etc.) — show instructions to open in external browser
  if (inApp) {
    return (
      <div className="login-container" role="main">
        <div className="login-form" style={{ textAlign: 'center' }}>
          {/* Language selector */}
          <div className="login-language-wrapper">
            <select
              value={SUPPORTED_LANGUAGES.find(l => l === i18n.language || i18n.language.startsWith(l + '-')) ?? 'en'}
              onChange={handleLanguageChange}
              aria-label={t('settings.displayLanguage', 'Display language')}
              className="login-language-select"
            >
              {SUPPORTED_LANGUAGES.map((code) => (
                <option key={code} value={code}>{LANGUAGE_LABELS[code] ?? code}</option>
              ))}
            </select>
          </div>
          <div className="login-logo-wrapper">
            <img src="/logo-1024_simple.png" alt="Heseda AI" className="login-logo-img" width={56} height={56} />
          </div>
          <h2 className="login-app-name" style={{ marginBottom: '8px' }}>
            {t('inAppBrowser.title')}
          </h2>
          <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.5', margin: '0 0 20px' }}>
            {t('inAppBrowser.description')}
          </p>
          <div style={{ textAlign: 'left', background: '#f9fafb', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
            <p style={{ fontWeight: 600, fontSize: '13px', color: '#374151', margin: '0 0 8px' }}>
              {t('inAppBrowser.instructions')}
            </p>
            <p style={{ fontSize: '13px', color: '#4b5563', margin: '0 0 6px', lineHeight: '1.5' }}>
              <span aria-hidden="true">📱</span> {t('inAppBrowser.iosStep')}
            </p>
            <p style={{ fontSize: '13px', color: '#4b5563', margin: '0', lineHeight: '1.5' }}>
              <span aria-hidden="true">📱</span> {t('inAppBrowser.androidStep')}
            </p>
          </div>
          <p style={{ color: '#6b7280', fontSize: '13px', margin: '0 0 12px' }}>
            {t('inAppBrowser.orCopyLink')}
          </p>
          <div style={{ background: '#f3f4f6', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', wordBreak: 'break-all', fontSize: '12px', color: '#6b7280' }}>
            {safeUrl}
          </div>
          <button
            type="button"
            onClick={handleCopyLink}
            style={{
              width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
              background: linkCopied ? '#10b981' : '#4285F4', color: '#fff',
              fontSize: '15px', fontWeight: 600, cursor: 'pointer',
              transition: 'background 0.2s',
            }}
          >
            {linkCopied ? `✓ ${t('inAppBrowser.copied')}` : <><span aria-hidden="true">📋</span> {t('inAppBrowser.copyLink')}</>}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container" role="main">
      <div className="login-form">
        {/* Language selector — top-right pill */}
        <div className="login-language-wrapper">
          <select
            value={SUPPORTED_LANGUAGES.find(l => l === i18n.language || i18n.language.startsWith(l + '-')) ?? 'en'}
            onChange={handleLanguageChange}
            data-testid="login-language-select"
            aria-label={t('settings.displayLanguage', 'Display language')}
            className="login-language-select"
          >
            {SUPPORTED_LANGUAGES.map((code) => (
              <option key={code} value={code}>{LANGUAGE_LABELS[code] ?? code}</option>
            ))}
          </select>
        </div>
        <div className="login-logo-wrapper">
          <img src="/logo-1024_simple.png" alt="Heseda AI" className="login-logo-img" width={56} height={56} />
        </div>
        <h2 className="login-app-name">Heseda AI</h2>
        <p className="login-tagline">{t('login.tagline')}</p>

        <div className="login-features">
          <div className="login-feature-item">
            <span className="login-feature-icon" aria-hidden="true">💬</span>
            <span className="login-feature-text">{t('login.features.aiChat')}</span>
          </div>
          <div className="login-feature-item">
            <span className="login-feature-icon" aria-hidden="true">📸</span>
            <span className="login-feature-text">{t('login.features.skinAnalysis', { defaultValue: 'AI Skin Analysis from Photo' })}</span>
          </div>
          <div className="login-feature-item">
            <span className="login-feature-icon" aria-hidden="true">🔍</span>
            <span className="login-feature-text">{t('login.features.productSearch')}</span>
          </div>
          <div className="login-feature-item">
            <span className="login-feature-icon" aria-hidden="true">🧴</span>
            <span className="login-feature-text">{t('login.features.personalizedRecs')}</span>
          </div>
        </div>
        <div className="login-social-proof">
          <span className="login-social-proof-stars" aria-hidden="true">★★★★★</span>
          <span className="sr-only">{t('login.fiveStarRating', '5 out of 5 stars')}</span>
          <p className="login-social-proof-text">
            {t('login.socialProof', 'Trusted by skincare enthusiasts in 11 languages')}
          </p>
        </div>

        <p className="login-free-badge">{t('login.freeBadge', '✓ Free to use — no subscription required')}</p>

        {loginError && <p className="error-message" role="alert">{loginError}</p>}
        <div className="oauth-section">
          <button type="button" onClick={onGoogleSignIn} className="oauth-button google-button" data-testid="login-google-btn">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" style={{ marginRight: '10px' }}>
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('login.continueWithGoogle')}
          </button>

          {/* Divider */}
          <div className="login-divider">
            <span className="login-divider-line" />
            <span className="login-divider-text">{t('login.orContinueWith', 'or')}</span>
            <span className="login-divider-line" />
          </div>

          {/* Email/Password toggle */}
          {!showEmailForm ? (
            <button
              type="button"
              onClick={() => setShowEmailForm(true)}
              className="oauth-button email-button"
              data-testid="login-email-toggle"
            >
              <span aria-hidden="true">✉️</span>
              <span>{t('login.continueWithEmail', 'Continue with Email')}</span>
            </button>
          ) : (
            <form onSubmit={handleEmailAuth} className="login-email-form" data-testid="login-email-form">
              {emailMode === 'signup' && (
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={t('login.email.namePlaceholder', 'Name')}
                  className="login-email-input"
                  autoComplete="name"
                  required
                />
              )}
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.email.emailPlaceholder', 'Email address')}
                className="login-email-input"
                autoComplete="email"
                required
              />
              {emailMode !== 'reset' && (
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('login.email.passwordPlaceholder', 'Password')}
                  className="login-email-input"
                  autoComplete={emailMode === 'signup' ? 'new-password' : 'current-password'}
                  required
                  minLength={6}
                />
              )}
              {emailError && <p className="error-message" role="alert">{emailError}</p>}
              {resetSent && emailMode === 'reset' && (
                <p className="login-email-success">{t('login.email.resetSent', 'Password reset email sent! Check your inbox.')}</p>
              )}
              <button
                type="submit"
                className="oauth-button email-submit-button"
                disabled={emailLoading}
              >
                {emailLoading
                  ? t('login.signingIn')
                  : emailMode === 'reset'
                    ? t('login.email.resetButton', 'Send Reset Link')
                    : emailMode === 'signup'
                      ? t('login.email.signUpButton', 'Create Account')
                      : t('login.email.signInButton', 'Sign In with Email')
                }
              </button>
              <div className="login-email-links">
                {emailMode === 'signin' && (
                  <>
                    <button type="button" onClick={() => { setEmailMode('signup'); setEmailError(null); }} className="login-email-link">
                      {t('login.email.noAccount', "Don't have an account? Sign up")}
                    </button>
                    <button type="button" onClick={() => { setEmailMode('reset'); setEmailError(null); }} className="login-email-link">
                      {t('login.email.forgotPassword', 'Forgot password?')}
                    </button>
                  </>
                )}
                {emailMode === 'signup' && (
                  <button type="button" onClick={() => { setEmailMode('signin'); setEmailError(null); }} className="login-email-link">
                    {t('login.email.hasAccount', 'Already have an account? Sign in')}
                  </button>
                )}
                {emailMode === 'reset' && (
                  <button type="button" onClick={() => { setEmailMode('signin'); setEmailError(null); setResetSent(false); }} className="login-email-link">
                    {t('login.email.backToSignIn', 'Back to sign in')}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
        <p className="login-legal-text">
          {t('login.agreementPrefix', 'By signing in, you agree to our')}{' '}
          <a href="/privacy" className="login-legal-link">
            {t('login.privacyPolicy', 'Privacy Policy')}
          </a>
          {t('login.andSpacer', ' ')}{t('login.and', 'and')}{t('login.andSpacer', ' ')}
          <a href="https://www.heseda.com/terms/" target="_blank" rel="noopener noreferrer" className="login-legal-link">
            {t('login.termsOfService', 'Terms of Service')}
          </a>
        </p>
        <p className="login-learn-more">
          <a
            href="https://www.heseda.com/how-it-works/?utm_source=heseda_ai&utm_medium=login&utm_campaign=learn_more"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('login.learnMore', 'Learn more about Heseda')} →
          </a>
        </p>
      </div>
    </div>
  );
}

export default LoginForm;
